import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const TVETTraining: React.FC = () => {
  const trainingPrograms = [
    {
      title: "Engineering Skills",
      description: "Comprehensive engineering training programs covering all aspects of modern engineering.",
      icon: "🔧",
      features: ["Mechanical engineering", "Electrical systems", "Automation technology"]
    },
    {
      title: "Technical Skills",
      description: "Hands-on technical training for practical engineering applications.",
      icon: "⚙️",
      features: ["Equipment operation", "Maintenance procedures", "Safety protocols"]
    },
    {
      title: "Digital Skills",
      description: "Modern digital skills training for the engineering workforce of the future.",
      icon: "💻",
      features: ["CAD/CAM systems", "Digital tools", "Software applications"]
    },
    {
      title: "Entrepreneurship",
      description: "Business and entrepreneurship training for engineering professionals.",
      icon: "💼",
      features: ["Business management", "Project planning", "Market analysis"]
    }
  ];

  const services = [
    "Comprehensive skills assessment",
    "Customized training programs",
    "Hands-on practical training",
    "Certification programs",
    "Job placement assistance",
    "Continuous professional development"
  ];

  const benefits = [
    {
      title: "Career Growth",
      description: "Enhanced skills that lead to better career opportunities and advancement",
      icon: "📈"
    },
    {
      title: "Industry Ready",
      description: "Training aligned with current industry standards and requirements",
      icon: "🏭"
    },
    {
      title: "Practical Skills",
      description: "Hands-on training that prepares students for real-world applications",
      icon: "🛠️"
    },
    {
      title: "Certification",
      description: "Recognized certifications that validate skills and knowledge",
      icon: "🎓"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-600 to-purple-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-spin-slow">
                <EngineeringIcon type="cog" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">TVET/Skills Training</h1>
            <p className="text-xl mb-8">
              Comprehensive technical and vocational education training center. Empowering the next generation 
              of engineers and skilled professionals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Skills Development</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Career Growth</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Industry Ready</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Training Programs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Training Programs</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive training programs designed to develop the skills needed for the engineering workforce of the future.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {trainingPrograms.map((program, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-pulley-bounce">{program.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{program.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{program.description}</p>
                <ul className="space-y-2">
                  {program.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-purple-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Benefits of Our Training Programs</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our TVET training programs deliver exceptional benefits for career development and professional growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors">
                <div className="text-4xl mb-4 animate-bounce-slow">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive training and development services to meet all your skills development needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="wrench" size="md" className="text-purple-500 mr-4" animated={false} />
                  <span className="font-semibold text-gray-800">{service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Empower Your Future with Skills</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our comprehensive training programs and develop the skills needed for the engineering workforce of the future. 
            Let's build a skilled workforce for Ekumfiman's industrial development.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="cog" size="sm" animated={false} />
              Enroll Now
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              View Programs
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TVETTraining; 